import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Plus, Trash2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Label } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const SUGGESTIONS = [
  "Which locations slipped this month and why?",
  "Who are my top and at-risk employees right now?",
  "What are customers complaining about most?",
  "Where should I focus coaching this week?",
];

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={`b${i}-${p}`} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
      : <span key={`t${i}-${p}`}>{p}</span>
  );
}

function Formatted({ text }) {
  const lines = (text || "").split("\n");
  return (
    <div className="space-y-1.5 text-sm leading-relaxed text-slate-700">
      {lines.map((ln, i) => {
        const t = ln.trim();
        if (!t) return <div key={i} className="h-1" />;
        if (/^[-*•]\s+/.test(t)) return <div key={i} className="flex gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#4338CA]" /><span>{renderInline(t.replace(/^[-*•]\s+/, ""))}</span></div>;
        return <p key={i}>{renderInline(t)}</p>;
      })}
    </div>
  );
}

export default function AiChat({ embedded = false }) {
  const { user } = useAuth();
  const isCorp = user.role === "corporate_admin";
  const [sessions, setSessions] = useState([]);
  const [activeSid, setActiveSid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [locId, setLocId] = useState("org");
  const [locations, setLocations] = useState([]);
  const endRef = useRef(null);

  const loadSessions = () => api.get("/ai/chat/sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { loadSessions(); if (isCorp) api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}); }, [isCorp]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const newChat = () => { setActiveSid(null); setMessages([]); setInput(""); setLocId("org"); };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/ai/chat/sessions/${sid}`);
      setActiveSid(sid); setMessages(data.messages);
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai/chat/sessions/${sid}`);
      if (sid === activeSid) newChat();
      loadSessions();
    } catch (e) { toast.error(apiError(e)); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const body = { message: msg, session_id: activeSid || undefined };
      if (!activeSid && isCorp && locId !== "org") body.location_id = locId;
      const { data } = await api.post("/ai/chat", body);
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
      if (!activeSid) { setActiveSid(data.session_id); loadSessions(); }
    } catch (e) {
      toast.error(apiError(e));
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    } finally { setSending(false); }
  };

  return (
    <div className={`flex gap-5 ${embedded ? "h-[calc(100vh-15rem)]" : "h-[calc(100vh-7rem)]"}`}>
      {/* Sessions */}
      <aside className="hidden w-64 shrink-0 flex-col rounded-2xl border border-slate-200 bg-white p-3 md:flex">
        <Button onClick={newChat} data-testid="new-chat" className="mb-3 w-full bg-[#4338CA] hover:bg-[#3730A3]"><Plus className="mr-1 h-4 w-4" /> New chat</Button>
        <div className="flex-1 space-y-1 overflow-y-auto sv-scroll">
          {sessions.length === 0 ? <p className="px-2 py-4 text-xs text-slate-400">Your past conversations will appear here.</p> :
            sessions.map((s) => (
              <button key={s.id} onClick={() => openSession(s.id)} data-testid={`chat-session-${s.id}`}
                className={`group flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${activeSid === s.id ? "bg-indigo-50 text-[#4338CA]" : "text-slate-600 hover:bg-slate-50"}`}>
                <MessageSquareText className="h-4 w-4 shrink-0 opacity-60" />
                <span className="flex-1 truncate">{s.title}</span>
                <Trash2 onClick={(e) => removeSession(s.id, e)} className="h-3.5 w-3.5 shrink-0 text-slate-300 opacity-0 hover:text-red-500 group-hover:opacity-100" />
              </button>
            ))}
        </div>
      </aside>

      {/* Thread */}
      <section className="flex min-w-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
          {embedded ? <p className="text-sm font-semibold text-slate-500">Ask anything about your live service data</p> : (
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#4338CA] text-white"><Sparkles className="h-4.5 w-4.5" /></span>
              <div><Label>Servantage AI</Label><h1 className="font-head text-lg font-bold text-slate-900">Ask Your Data</h1></div>
            </div>
          )}
          {isCorp && !activeSid && (
            <Select value={locId} onValueChange={setLocId}>
              <SelectTrigger className="w-44" data-testid="chat-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="org">All locations</SelectItem>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5 sv-scroll" data-testid="chat-thread">
          {messages.length === 0 && !sending && (
            <div className="mx-auto max-w-lg py-8 text-center">
              <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-[#4338CA]"><Sparkles className="h-7 w-7" /></span>
              <h2 className="font-head text-xl font-bold text-slate-900">Ask anything about your service data</h2>
              <p className="mt-1 text-sm text-slate-500">I answer from your live ratings, locations, employees and feedback.</p>
              <div className="mt-5 grid gap-2 sm:grid-cols-2">
                {SUGGESTIONS.map((q) => (
                  <button key={q} onClick={() => send(q)} data-testid="chat-suggestion"
                    className="rounded-xl border border-slate-200 p-3 text-left text-sm text-slate-600 transition-colors hover:border-indigo-200 hover:bg-indigo-50/50">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${m.role === "user" ? "bg-[#4338CA] text-white" : "border border-slate-200 bg-slate-50"}`}>
                {m.role === "user" ? <p className="text-sm leading-relaxed">{m.content}</p> : <Formatted text={m.content} />}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="chat-typing">
              <div className="flex items-center gap-1.5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#4338CA] [animation-delay:-0.3s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#4338CA] [animation-delay:-0.15s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#4338CA]" />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-slate-100 p-3">
          <div className="flex items-end gap-2">
            <textarea
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about locations, employees, ratings, complaints…" rows={1}
              data-testid="chat-input"
              className="max-h-32 flex-1 resize-none rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#4338CA]" />
            <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="chat-send" className="h-11 bg-[#4338CA] hover:bg-[#3730A3]">
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-1.5 px-1 text-[11px] text-slate-400">Servantage AI answers from your live data. Double-check important decisions.</p>
        </div>
      </section>
    </div>
  );
}
